import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Camera, X, Flashlight, FlashlightOff, Focus, Loader2, Aperture } from "lucide-react";

import { haptic } from "@/lib/floor";

// In-page LIVE BURST camera (same getUserMedia tech as the scan field). The camera opens
// once and STAYS LIVE: tap the shutter to grab a full-resolution frame, it stays armed for
// the next shot — no re-tap, no OS "OK/Retake" dialog. We request the device's MAX resolution
// (up to 4K) and capture at the live track's native size, so downstream 2048px downscale gets
// a high-res source (sale-listing quality). Torch + tap-to-refocus carry over from ScanField.
export function LiveCameraCapture({ onCapture, onClose, testidPrefix = "burst" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const mountedRef = useRef(true);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");
  const [dims, setDims] = useState(null);       // {w, h} actual capture resolution
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [focusSupported, setFocusSupported] = useState(false);
  const [flash, setFlash] = useState(false);     // brief shutter flash

  const stop = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    streamRef.current = null; trackRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          // Ask for the highest the device will give; the browser clamps to its real max.
          video: { facingMode: { ideal: "environment" },
                   width: { ideal: 3840 }, height: { ideal: 2160 } },
        });
        if (!mountedRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          v.setAttribute("playsinline", "true");
          await v.play().catch(() => {});
        }
        try {
          const caps = track.getCapabilities ? track.getCapabilities() : {};
          setTorchSupported(!!caps.torch);
          setFocusSupported(Array.isArray(caps.focusMode) ? caps.focusMode.length > 0 : !!caps.focusMode);
        } catch { /* capabilities not exposed */ }
        const s = track.getSettings ? track.getSettings() : {};
        setDims({ w: s.width || v?.videoWidth || 0, h: s.height || v?.videoHeight || 0 });
        setReady(true);
      } catch (e) {
        const detail = e && e.name ? `${e.name}${e.message ? ": " + e.message : ""}` : String(e);
        setErr(`Camera error — ${detail}. Allow camera access, close other apps using the camera, or use the Photograph / Library fallback.`);
      }
    })();
    return () => { mountedRef.current = false; stop(); };
  }, [stop]);

  const shoot = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const w = v.videoWidth, h = v.videoHeight;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(v, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `burst_${Date.now()}.jpg`, { type: "image/jpeg" });
      onCapture?.(file);
      setFlash(true); setTimeout(() => mountedRef.current && setFlash(false), 140);
      haptic(15);
    }, "image/jpeg", 0.95);   // near-lossless client encode; server downscales to the 2048 target
  };

  const toggleTorch = async () => {
    const t = trackRef.current; if (!t) return;
    const next = !torchOn;
    try { await t.applyConstraints({ advanced: [{ torch: next }] }); setTorchOn(next); }
    catch { /* torch not available */ }
  };
  const refocus = async () => {
    const t = trackRef.current; if (!t || !focusSupported) return;
    try { await t.applyConstraints({ advanced: [{ focusMode: "continuous" }] }); } catch { /* ignore */ }
  };

  return (
    <div data-testid={`${testidPrefix}-live`} className="rounded-2xl border border-slate-800 bg-black overflow-hidden">
      <div className="relative">
        <video ref={videoRef} data-testid={`${testidPrefix}-video`} muted playsInline
          onClick={focusSupported ? refocus : undefined}
          className={`w-full max-h-[60dvh] object-contain bg-black ${focusSupported ? "cursor-pointer" : ""}`} />
        {flash && <div className="absolute inset-0 bg-white/80 pointer-events-none" />}
        {dims?.w > 0 && (
          <span data-testid={`${testidPrefix}-res`} className="absolute top-2 left-2 text-[11px] font-mono bg-black/60 text-white px-2 py-0.5 rounded">
            Capturing at {dims.w}×{dims.h}
          </span>
        )}
        {focusSupported && (
          <span className="absolute bottom-2 left-2 text-[10px] font-mono bg-black/60 text-white px-1.5 py-0.5 rounded pointer-events-none">Tap video to refocus</span>
        )}
        {!ready && !err && (
          <div className="absolute inset-0 flex items-center justify-center text-white gap-2"><Loader2 className="w-5 h-5 animate-spin" />Starting camera…</div>
        )}
      </div>
      {err ? (
        <div className="p-3">
          <p data-testid={`${testidPrefix}-error`} className="text-xs text-[#ffb4b4] mb-2">{err}</p>
          <Button type="button" variant="outline" data-testid={`${testidPrefix}-error-close`} onClick={() => { stop(); onClose?.(); }}
            className="h-10 rounded-lg border-slate-600 text-white w-full">
            <X className="w-4 h-4 mr-1.5" />Use the Photograph / Library fallback instead
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3">
          <Button type="button" data-testid={`${testidPrefix}-shutter`} onClick={shoot} disabled={!ready}
            className="flex-1 h-14 rounded-xl bg-[#B01E28] hover:bg-[#8f1820] text-white text-base font-bold">
            <Aperture className="w-6 h-6 mr-2" />Shoot
          </Button>
          {torchSupported && (
            <Button type="button" variant="outline" data-testid={`${testidPrefix}-torch`} onClick={toggleTorch}
              className={`h-14 w-14 rounded-xl p-0 ${torchOn ? "border-amber-400 text-amber-300 bg-amber-500/10" : "border-slate-600 text-white"}`}>
              {torchOn ? <FlashlightOff className="w-5 h-5" /> : <Flashlight className="w-5 h-5" />}
            </Button>
          )}
          {focusSupported && (
            <Button type="button" variant="outline" data-testid={`${testidPrefix}-focus`} onClick={refocus}
              className="h-14 w-14 rounded-xl p-0 border-slate-600 text-white"><Focus className="w-5 h-5" /></Button>
          )}
          <Button type="button" variant="outline" data-testid={`${testidPrefix}-close`} onClick={() => { stop(); onClose?.(); }}
            className="h-14 w-14 rounded-xl p-0 border-slate-600 text-white"><X className="w-5 h-5" /></Button>
        </div>
      )}
    </div>
  );
}
